import { Controller, useForm } from "react-hook-form";
import {
  alpha,
  Avatar,
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  MenuItem,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import { useContext, useEffect, useRef, useState } from "react";
import axios from "../../api/axios";
import AuthContext from "../../context/AuthProvider";
import { useLocation } from "react-router-dom";
import logo from "../../utils/images/ridePlus.png";
import ProfilePicture from "../profile/profile-picture";
import { queryClient } from "../../App";
import { phoneRegex } from "../../utils/validationPattern";
import useOtpResendCooldown from "../../hooks/useOtpResendCooldown";
import OtpResendButton from "../../components/otpResendButton";
import AuthLayout from "../../components/auth/AuthLayout";
import AuthTextField from "../../components/auth/AuthTextField";
import PrimaryButton from "../../components/auth/PrimaryButton";
import SecondaryButton from "../../components/auth/SecondaryButton";

function RegisterWithWeb({ setOpenRegister }) {
  // USESTATE & USEREF
  const [openDrawer, setOpenDrawer] = useState(false);
  const [phone, setPhone] = useState("");
  const [file, setFile] = useState("");
  const [open, setOpen] = useState(false);
  const [requestOtp, setRequestOtp] = useState(false);

  const [tosAndPPUrl, setTosAndPPUrl] = useState(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const userData = useRef({});

  // Cooldown hook for OTP resend functionality
  const cooldownHook = useOtpResendCooldown("register", userData.current?.phone);

  // SNACKBAR
  const { enqueueSnackbar } = useSnackbar();

  const location = useLocation();
  const { redirectUri } = useContext(AuthContext);
  const searchParams = new URLSearchParams(location.search);
  const redirectSearchParams = new URLSearchParams(redirectUri?.search || "");
  const clientIdFromUrl =
    searchParams.get("client_id") || searchParams.get("clientId");
  const tenantIdFromUrl =
    searchParams.get("tenant_id") || searchParams.get("tenantId");
  const isConsentFlow = !!clientIdFromUrl && !!tenantIdFromUrl;
  const clientIdForOtp =
    clientIdFromUrl ||
    redirectSearchParams.get("client_id") ||
    redirectSearchParams.get("clientId");

  // REACT HOOK FORM
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  // MUTATION & USEQUERY
  const { mutate, isLoading, data, error } = useMutation((data) => {
    return axios.post("/register", data, {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
  });
  const {
    isFetching: otpIsFetching,
    data: otpData,
    error: otpError,
  } = useQuery(
    ["otp_for_web", userData.current?.phone, clientIdForOtp],
    () =>
      axios.get("/otp", {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          type: "signup",
          phone: userData.current.phone,
          ...(clientIdForOtp && { client_id: clientIdForOtp }),
        },
      }),
    {
      refetchOnWindowFocus: false,
      enabled: requestOtp && userData.current?.phone !== undefined,
      retry: false,
    }
  );

  const { isFetching: tosLinksIsFetching, isLoading: tosLinksIsLoading } =
    useQuery(
      ["tos-pp-url", clientIdFromUrl, tenantIdFromUrl, isConsentFlow],
      () =>
        axios.get("/clients/tos-pp-url", {
          params: {
            client_id: clientIdFromUrl,
            tenant_id: tenantIdFromUrl,
          },
        }),
      {
        enabled: isConsentFlow,
        retry: false,
        onSuccess: (response) => {
          const data = response?.data?.data || response?.data;
          const privacyPolicyUrl = data?.privacy_policy_url || "";
          const termsOfServiceUrl = data?.terms_of_service_url || "";
          if (privacyPolicyUrl || termsOfServiceUrl) {
            setTosAndPPUrl({
              privacy_policy_url: privacyPolicyUrl,
              terms_of_service_url: termsOfServiceUrl,
            });
            setAgreeToTerms(false);
            return;
          }
          setTosAndPPUrl(null);
          setAgreeToTerms(false);
        },
        onError: () => {
          setTosAndPPUrl(null);
          setAgreeToTerms(false);
        },
      }
    );

  // USEEFFECT
  useEffect(() => {
    if (data) {
      enqueueSnackbar(`You're succesfully registered!`, {
        variant: "success",
        preventDuplicate: true,
        autoHideDuration: 2000,
      });
      queryClient.clear();
      setOpenRegister(false);
    }
  }, [data, enqueueSnackbar, setOpenRegister]);

  useEffect(() => {
    if (otpData && userData.current.phone !== undefined) {
      enqueueSnackbar(`Code sent to your phone!`, {
        variant: "success",
        preventDuplicate: true,
        autoHideDuration: 2000,
      });
      cooldownHook.startCooldown(); // Start cooldown after successful OTP request
      setRequestOtp(false);
      queryClient.clear();
    }
  }, [enqueueSnackbar, otpData, cooldownHook]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(
        error?.response?.data?.error?.message ||
        error?.message ||
        "Network Error!",
        {
          variant: "error",
          preventDuplicate: true,
          autoHideDuration: 2000,
        }
      );
    }
    if (otpError && userData.current.phone !== undefined) {
      enqueueSnackbar(
        otpError?.response?.data?.error?.message ||
        otpError?.message ||
        "Network Error!",
        {
          variant: "error",
          preventDuplicate: true,
          autoHideDuration: 2000,
        }
      );
    }
  }, [enqueueSnackbar, error, otpError]);

  // EVENT HANDLERS & HELPERS
  const handleClick = () => {
    setOpenRegister(false);
  };
  const handleRegister = (form) => {
    if (isConsentFlow && (tosLinksIsFetching || tosLinksIsLoading)) {
      enqueueSnackbar("Please wait while loading Privacy Policy and Terms.", {
        variant: "info",
        preventDuplicate: true,
        autoHideDuration: 2500,
      });
      return;
    }

    if (tosAndPPUrl && !agreeToTerms) {
      enqueueSnackbar(
        "Please agree to the Privacy Policy and Terms to continue.",
        {
          variant: "warning",
          preventDuplicate: true,
          autoHideDuration: 2500,
        }
      );
      return;
    }

    setOpenDrawer(true);
    setPhone(form?.phone);
    userData.current = form;
    setRequestOtp(true);
  };
  const handleSignup = (form) => {
    userData.current = { ...userData.current, otp: form.otp };
    const dataToBeSent =
      file !== ""
        ? { ...userData.current, profile_picture: file }
        : userData.current;
    mutate(dataToBeSent);
  };

  const brandingAvatar = (
    <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
      <Avatar sx={{ width: "90px", height: "72px", borderRadius: 1 }} src={logo} />
    </Box>
  );

  // RENDER — OTP verification step
  if (openDrawer && !otpIsFetching && !otpError) {
    return (
      <AuthLayout
        title="Enter a code"
        header={brandingAvatar}
        onSubmit={handleSubmit(handleSignup)}
        footer={
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: 1.5,
            }}
          >
            <SecondaryButton onClick={() => setOpenDrawer(false)}>
              Back
            </SecondaryButton>
            <PrimaryButton type="submit" loading={isLoading}>
              Submit
            </PrimaryButton>
          </Box>
        }
      >
        <Typography
          sx={{
            fontSize: { xs: 14, sm: 15 },
            mb: 2,
            textAlign: "center",
            color: "text.secondary",
            lineHeight: 1.6,
          }}
        >
          Please fill the code sent to your phone number:{" "}
          <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
            {phone}
          </Box>{" "}
          via SMS.
        </Typography>
        <Controller
          name="otp"
          control={control}
          rules={{ required: "Code is required" }}
          render={({ field }) => (
            <AuthTextField
              variant="numeric"
              label="Code"
              autoFocus
              error={errors?.otp}
              helperText={errors?.otp ? errors?.otp?.message : ""}
              {...field}
            />
          )}
        />
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: (theme) =>
              theme.palette.mode === "light"
                ? "primary.lighter"
                : alpha(theme.palette.primary.main, 0.1),
            border: "1px solid",
            borderColor: "primary.main",
          }}
        >
          <OtpResendButton
            cooldownHook={cooldownHook}
            onResend={() => setRequestOtp(true)}
            isLoading={otpIsFetching}
            size="small"
            showMessage={true}
          />
        </Box>
      </AuthLayout>
    );
  }

  // RENDER — registration form
  return (
    <AuthLayout
      title="Create your account"
      header={brandingAvatar}
      onSubmit={handleSubmit(handleRegister)}
      footer={
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: 1.5,
          }}
        >
          <SecondaryButton onClick={handleClick}>Back</SecondaryButton>
          <PrimaryButton
            type="submit"
            loading={otpIsFetching}
            disabled={isConsentFlow && tosLinksIsFetching}
          >
            Next
          </PrimaryButton>
        </Box>
      }
    >
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <ProfilePicture
          edit={true}
          isOpen={open}
          setIsOpen={setOpen}
          imageSrc={file}
          setFile={setFile}
        />
      </Box>
      <Controller
        name="first_name"
        control={control}
        rules={{ required: "First Name is required" }}
        render={({ field }) => (
          <AuthTextField
            label="First Name"
            autoComplete="given-name"
            defaultValue={userData.current?.first_name}
            error={errors?.first_name}
            helperText={errors?.first_name ? errors.first_name.message : null}
            {...field}
          />
        )}
      />
      <Controller
        name="middle_name"
        control={control}
        rules={{ required: "Middle Name is required" }}
        render={({ field }) => (
          <AuthTextField
            label="Middle Name"
            autoComplete="additional-name"
            defaultValue={userData.current?.middle_name}
            error={errors?.middle_name}
            helperText={errors?.middle_name ? errors.middle_name.message : null}
            {...field}
          />
        )}
      />
      <Controller
        name="last_name"
        control={control}
        rules={{ required: "Last Name is required" }}
        render={({ field }) => (
          <AuthTextField
            label="Last Name"
            autoComplete="family-name"
            defaultValue={userData.current?.last_name}
            error={errors?.last_name}
            helperText={errors?.last_name ? errors.last_name.message : null}
            {...field}
          />
        )}
      />
      <Controller
        name="gender"
        control={control}
        rules={{ required: "Gender is required" }}
        render={({ field }) => (
          <AuthTextField
            label="Gender"
            select
            autoComplete="sex"
            defaultValue={userData.current?.gender || ""}
            error={errors?.gender}
            helperText={errors?.gender ? errors.gender.message : null}
            {...field}
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
          </AuthTextField>
        )}
      />
      <Controller
        name="phone"
        control={control}
        rules={{
          required: "Phone is required",
          pattern: { value: phoneRegex, message: "Invalid input" },
        }}
        render={({ field }) => (
          <AuthTextField
            variant="phone"
            label="Phone"
            defaultValue={userData.current?.phone}
            error={errors?.phone}
            helperText={errors?.phone ? errors.phone.message : null}
            {...field}
          />
        )}
      />
      {tosAndPPUrl && (
        <Box sx={{ mb: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                sx={{ p: 0.5, mt: 0.25 }}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                I agree to the{" "}
                {tosAndPPUrl?.privacy_policy_url && (
                  <Link
                    href={tosAndPPUrl.privacy_policy_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </Link>
                )}
                {tosAndPPUrl?.privacy_policy_url &&
                tosAndPPUrl?.terms_of_service_url
                  ? " and "
                  : ""}
                {tosAndPPUrl?.terms_of_service_url && (
                  <Link
                    href={tosAndPPUrl.terms_of_service_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </Link>
                )}
                .
              </Typography>
            }
            sx={{ alignItems: "center", m: 0, gap: 1 }}
          />
        </Box>
      )}
    </AuthLayout>
  );
}
export default RegisterWithWeb;
